import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import axiosInstance, { getErrorMessage } from '../../../utils/axios';
import CRUD from '../../../utils/Crud';
import ReferralHistory from '../../@dashboard/user/profile/ReferralHistory';
import ContactReferrals from '../../@dashboard/user/profile/ContactReferrals';
import ProfileSettings from '../../@dashboard/user/profile/ProfileSettings';
import BeneficiariesTab from './BeneficiariesTab';
import BankAccountsPlaceholder from './BankAccountsPlaceholder';
import { TAB_OPTIONS } from './constants';
import ControlsTab from './ControlsTab';
import DevicesTab from './DevicesTab';
import ManageUserDialogHeader from './ManageUserDialogHeader';
import ManageUserDialogTabs from './ManageUserDialogTabs';
import ImagePreviewDialog from './ImagePreviewDialog';
import PolicyTab from './PolicyTab';
import ProfileOverviewTab from './ProfileOverviewTab';
import UserTransactionsTab from './UserTransactionsTab';
import { buildUserPatch } from './utils';
import SavedBankAccount from './SavedBankAccount';

function resolveInitialTab(tab) {
  return TAB_OPTIONS.some((item) => item.value === tab) ? tab : 'profile';
}

export default function ManageUserProfileView({
  currentTab,
  onClose,
  onProfileNameChange,
  onTabChange,
  showCloseButton = false,
  user,
}) {
  const [internalTab, setInternalTab] = useState(resolveInitialTab(currentTab));
  const [state, setState] = useState({
    user: user || {},
    wallet: {},
    pending_balance: 0,
    settlement_banks: [],
    loading: false,
    loadComplete: false,
    error: null,
    devices: [],
    account_status: '',
  });
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);

  const activeTab = currentTab ? resolveInitialTab(currentTab) : internalTab;

  const profileName = useMemo(() => {
    const currentUser = state.user || {};
    return currentUser.display_name || `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'User Profile';
  }, [state.user]);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      user: user || {},
      wallet: {},
      pending_balance: 0,
      settlement_banks: [],
      loading: false,
      loadComplete: false,
      error: null,
      devices: [],
      account_status: '',
    }));
    setIsImagePreviewOpen(false);
  }, [user]);

  useEffect(() => {
    if (!currentTab) {
      return;
    }

    setInternalTab(resolveInitialTab(currentTab));
  }, [currentTab]);

  useEffect(() => {
    if (onProfileNameChange) {
      onProfileNameChange(profileName);
    }
  }, [onProfileNameChange, profileName]);

  const loadProfile = useCallback(async () => {
    if (!user?.user_id) {
      return;
    }

    setState((prev) => ({
      ...prev,
      loading: true,
      loadComplete: false,
      error: null,
      user: {
        ...prev.user,
        ...user,
      },
    }));

    try {
      const response = await axiosInstance.get(`${CRUD.user.details}?user_id=${user.user_id}`);
      const payload = response.data || {};
      const explicitUser = payload.user || payload.basic || payload.profile || {};
      const fallbackUser = buildUserPatch(payload);

      setState((prev) => ({
        ...prev,
        ...payload,
        user: {
          ...prev.user,
          ...user,
          ...fallbackUser,
          ...explicitUser,
        },
        account_status: payload.account_status || explicitUser.account_status || prev.account_status,
        loading: false,
        loadComplete: true,
        error: null,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        loadComplete: false,
        error: getErrorMessage(error),
      }));
    }
  }, [user]);

  useEffect(() => {
    if (user?.user_id) {
      loadProfile();
    }
  }, [loadProfile, user?.user_id]);

  const handleTabSelection = useCallback(
    (event, value) => {
      const nextTab = resolveInitialTab(value);

      if (!currentTab) {
        setInternalTab(nextTab);
      }

      if (onTabChange) {
        onTabChange(event, nextTab);
      }
    },
    [currentTab, onTabChange]
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileOverviewTab state={state} />;
      case 'transaction':
        return <UserTransactionsTab user={state.user} />;
      case 'policy':
        return <PolicyTab userId={state.user.user_id} onRetry={() => {}} />;
      case 'controls':
        return <ControlsTab userId={state.user.user_id} />;
      case 'bank-accounts':
        return <SavedBankAccount userId={state.user.user_id} />;
      case 'beneficiaries':
        return <BeneficiariesTab userId={state.user.user_id} />;
      case 'referrals':
        return <ReferralHistory user={state.user} />;
      case 'invites':
        return <ContactReferrals user={state.user} />;
      case 'security':
        return (
          <ProfileSettings
            user={state.user}
            status={state.account_status}
            onStatusChanged={(accountStatus) =>
              setState((prev) => ({
                ...prev,
                account_status: accountStatus,
              }))
            }
          />
        );
      case 'devices':
        return <DevicesTab state={state} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <ManageUserDialogHeader
          handleClose={onClose}
          onAvatarClick={() => setIsImagePreviewOpen(true)}
          profileName={profileName}
          showCloseButton={showCloseButton}
          user={state.user}
        />

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <ManageUserDialogTabs
            currentTab={activeTab}
            kycStatus={state.kyc_status}
            onChange={handleTabSelection}
            tabs={TAB_OPTIONS}
          />

          <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>{renderContent()}</Box>
          </Box>
        </Box>
      </Box>

      {state.user?.image_url ? (
        <ImagePreviewDialog
          imageUrl={state.user.image_url}
          open={isImagePreviewOpen}
          onClose={() => setIsImagePreviewOpen(false)}
          title={profileName}
        />
      ) : null}
    </>
  );
}

ManageUserProfileView.propTypes = {
  currentTab: PropTypes.string,
  onClose: PropTypes.func,
  onProfileNameChange: PropTypes.func,
  onTabChange: PropTypes.func,
  showCloseButton: PropTypes.bool,
  user: PropTypes.object.isRequired,
};
